import { useEffect } from "react";
import { Link, isRouteErrorResponse, useRouteError } from "react-router";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { buildLocalizedPath, type Lang } from "~/config/route-config";
import { Button } from "~/components/ui";
import { boundaryReports } from "~/lib/error-ownership";
import { scrubPii } from "~/lib/pii-scrub";
import { safeReferrer } from "~/lib/incident";
import { isHydrating } from "~/lib/hydration-state";
import { useLeafRouteId } from "~/lib/boundary-route-id";

/**
 * T775 — why this reads `useRouteError()` instead of trusting its prop.
 *
 * React Router never passes props to a route's `ErrorBoundary` at runtime: the
 * route property becomes `createElement(route.ErrorBoundary)` — no props, ever
 * (`mapRouteProperties`). The `error` prop the docs describe is injected at BUILD
 * time, by a Babel transform in the Vite plugin that recognises only declaration
 * shapes: `export default …`, `export function ErrorBoundary`,
 * `export const ErrorBoundary =`. It skips a re-export, because a re-export has no
 * declaration node to wrap.
 *
 * All 69 of this app's route and layout modules are re-exports:
 *
 *     export { RouteErrorBoundary as ErrorBoundary } from "~/components/route-error-boundary";
 *
 * so this component saw `error === undefined` on every render, on every route, in
 * every browser — and reported `UnknownError: undefined` to Sentry 483 times while
 * the real error sat one hook call away. Reading the error here repairs all 69 at
 * once and cannot regress when someone writes the 70th re-export.
 *
 * The prop is kept as an explicit override (tests, and the day a caller is
 * decorated after all) and wins only when it is not `undefined`.
 */
export function RouteErrorBoundary({ error: errorProp }: { error?: unknown }) {
  const { t, i18n } = useTranslation("common");
  // `?? undefined` folds one sentinel into the other. `useRouteError()` answers
  // "nothing here" in two different shapes — `undefined` when the router's `errors`
  // map has no entry, and `null` (the RouteErrorContext default) when the component
  // is not inside a boundary render at all. Neither can be a real routed error:
  // React Router renders the boundary only for a *truthy* `errors[routeId]`, so a
  // thrown `null` gets the normal element and a thrown `undefined` trips its own
  // invariant before any boundary mounts. One absence, one tag value.
  //
  // This hook DOES throw outside a data router — it goes through
  // `useDataRouterState` — so `useLeafRouteId()` below buys this component no
  // crash-safety it does not already lack. It is kept for symmetry with root's
  // boundary, where it is the only such hook and therefore the whole point. The
  // throw here is unavoidable: replicating `useRouteError()` safely would need
  // `RouteErrorContext`, which react-router does not export, and without that
  // context a render error caught by `componentDidCatch` would be invisible.
  const routeError = useRouteError() ?? undefined;
  const error = errorProp !== undefined ? errorProp : routeError;
  const routeId = useLeafRouteId();
  const lang: Lang = i18n.language?.startsWith("en") ? "en" : "es";
  const homePath = buildLocalizedPath("home", lang);
  const supportPath = buildLocalizedPath("support", lang);

  // T775 — the status line stays UNRENDERED, and that is a decision, not an oversight.
  //
  // `error_boundary.status_line` ("Código {{status}}") has been in this component since it
  // was written, behind `isRouteErrorResponse(error)` — which was permanently false while the
  // error never arrived, so no member has ever seen it. Repairing the error made it reachable
  // for the first time, and the comprehension judge measured what that costs: the same card
  // WITHOUT the line passes (2 of 5 blocked), WITH it fails (4 of 5), and one reader is
  // blocked by the line itself — "no sé si ese 400 es para mí o para ellos".
  //
  // A bare HTTP status answers a question she is not asking. What it was meant to do — say
  // which failure this was — is now done properly, in the Sentry tags below, where it is read
  // by someone who can act on it. Whether she should see a code at all, and in what words,
  // belongs to T38, which owns this surface; the judge's quote is its brief.
  //
  // So this ticket keeps the promise its own spec makes: it changes what Sentry learns, not
  // what she reads. `status` is therefore not computed.
  let detail: string | undefined;

  if (isRouteErrorResponse(error)) {
    if (typeof error.data === "string") detail = error.data;
  } else if (import.meta.env.DEV && error instanceof Error) {
    detail = error.message;
  }

  // T806 — what this boundary reports is decided by `~/lib/error-ownership`, the
  // one file that declares who owns which error class. Not by this comment.
  //
  // The comment that used to stand here said `isRouteErrorResponse` errors were
  // "already captured server-side", and the server said the mirror image of it
  // ("a thrown Response is never a fault worth an incident"). Each file described
  // the other's behaviour instead of pointing at a shared declaration, so the
  // class between them — an unexpected 400, the T805 bug — reached Sentry from
  // neither side, for two tickets, while both comments stayed defensible.
  //
  // `boundaryReports()` says no to every status response because all three of
  // those classes are owned elsewhere (or by nobody, deliberately); it says yes to
  // a client render error, which this component is the only witness to. Change
  // that answer in the table, never here.
  useEffect(() => {
    if (!boundaryReports(error)) return;
    try {
      const message = error instanceof Error ? error.message : String(error);
      const stack = error instanceof Error ? (error.stack ?? "") : "";
      const scrubbedMessage = scrubPii(message).slice(0, 500);
      const scrubbedStack = scrubPii(stack).slice(0, 2000);
      const synthetic = new Error(scrubbedMessage);
      synthetic.name = error instanceof Error ? error.constructor.name : "UnknownError";
      synthetic.stack = scrubbedStack;
      Sentry.withScope((scope) => {
        scope.setTag("source", "client");
        scope.setTag("boundary", "route");
        scope.setTag("locale", lang);
        // T775 — the four tags that make a future `undefined` triageable instead of
        // countable. `JAVASCRIPT-REACT-3` had 483 events and could not answer any of
        // these: which screen, what arrived, where she came from, and whether the app
        // had even finished starting.
        scope.setTag("route_id", routeId ?? "unknown");
        scope.setTag("error_type", error === null ? "null" : typeof error);
        scope.setTag("from_path", safeReferrer(document.referrer) || "none");
        scope.setTag("hydrating", String(isHydrating()));
        Sentry.captureException(synthetic);
      });
    } catch {
      // Fire-and-forget; never let observability noise break the fallback UI.
    }
  }, [error, lang, routeId]);

  return (
    <div className="flex flex-1 flex-col items-center justify-center bg-primary px-6 py-16 text-textMain">
      <div className="w-full max-w-md border border-borderLight bg-primary p-8 text-center shadow-sm">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-textSecondary">
          {t("error_boundary.kicker")}
        </p>
        <h1 className="mt-3 font-serif text-2xl tracking-tight md:text-3xl">
          {t("error_boundary.title")}
        </h1>
        <p className="mt-4 text-sm font-light leading-relaxed text-textSecondary">
          {t("error_boundary.body")}
        </p>
        {detail && import.meta.env.DEV ? (
          <pre className="mt-4 max-h-40 overflow-auto whitespace-pre-wrap rounded border border-borderLight bg-surfaceMuted p-3 text-left text-xs text-textMain">
            {detail}
          </pre>
        ) : null}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link
            to={homePath}
            className="inline-flex items-center justify-center border border-textMain bg-darkBg px-6 py-3 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-primary hover:text-textMain"
          >
            {t("error_boundary.cta_home")}
          </Link>
          <Link
            to={supportPath}
            className="inline-flex items-center justify-center border border-textMain bg-primary px-6 py-3 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-textMain transition-colors hover:bg-darkBg hover:text-white"
          >
            {t("error_boundary.cta_support")}
          </Link>
        </div>
        <Button
          type="button"
          variant="text"
          onClick={() => window.location.reload()}
          className="mt-6 text-[10px] font-bold uppercase tracking-widest text-textSecondary underline transition-opacity hover:opacity-60"
        >
          {t("error_boundary.retry")}
        </Button>
      </div>
    </div>
  );
}
